import {
  deployAppImage,
  fetchApp,
  fetchSettingById,
  selectAppById,
  selectAppDeploymentType,
  selectSettingById,
} from "@app/deploy";
import {
  fetchDeploymentsByAppId,
  selectDeploymentsByAppId,
} from "@app/deployment";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { appActivityUrl, appDeployResumeUrl } from "@app/routes";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  ButtonIcon,
  ButtonLinkDocs,
  DeploymentsTable,
  DetailPageSections,
  Disclosure,
  ExternalLink,
  Form,
  FormGroup,
  Group,
  IconEyeClosed,
  IconEyeOpen,
  IconPlusCircle,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
  ModalWrapper,
  PreCode,
  listToInvertedTextColor,
  tokens,
} from "../shared";

function ImageDeployForm({
  appId,
  settingId,
  showMigrationBanner,
}: {
  appId: string;
  settingId: string;
  showMigrationBanner: boolean;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useQuery(fetchSettingById({ id: settingId, withSensitiveExtras: true }));
  const setting = useSelector((s) => selectSettingById(s, { id: settingId }));

  const [dockerImage, setDockerImage] = useState("");
  const [registryUsername, setRegistryUsername] = useState("");
  const [registryPassword, setRegistryPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    if (!setting.id) return;
    initialized.current = true;
    setDockerImage(setting.settings.APTIBLE_DOCKER_IMAGE ?? "");
    setRegistryUsername(
      setting.sensitiveSettings.APTIBLE_PRIVATE_REGISTRY_USERNAME ?? "",
    );
    setRegistryPassword(
      setting.sensitiveSettings.APTIBLE_PRIVATE_REGISTRY_PASSWORD ?? "",
    );
  }, [setting.id, setting.settings, setting.sensitiveSettings]);

  const action = deployAppImage({
    appId,
    dockerImage,
    registryUsername,
    registryPassword,
  });
  const loader = useLoader(deployAppImage);

  useLoaderSuccess(loader, () => {
    navigate(appActivityUrl(appId));
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(action);
  };

  const onClose = () => setIsOpen(false);

  return (
    <Box>
      <Group size="sm">
        <div className="flex items-center justify-between">
          <h3 className={tokens.type.h3}>Deploy via Docker Image</h3>
          <ButtonLinkDocs href="https://www.aptible.com/docs/core-concepts/apps/deploying-apps/image/overview#deploy-from-docker-image" />
        </div>
        {showMigrationBanner ? (
          <Banner variant="info">
            You've previously deployed this app via Git.{" "}
            <ExternalLink
              href="https://www.aptible.com/docs/how-to-guides/app-guides/migrate-dockerfile-to-direct-image-deploy"
              variant="default"
            >
              Review the docs for steps for migrating to Docker Image before
              proceeding.
            </ExternalLink>
          </Banner>
        ) : null}
        <hr />
        <p>
          Deploy a pre-built Docker image directly from a public or private
          registry.
        </p>
        <div>
          <Button className="mt-2" onClick={() => setIsOpen(true)}>
            Deploy Docker Image
          </Button>
        </div>
        <div>
          <PreCode
            segments={listToInvertedTextColor([
              "aptible deploy",
              "--app",
              "$APP_HANDLE",
              "--docker-image",
              "$DOCKER_IMAGE",
            ])}
            allowCopy
          />
        </div>
      </Group>

      {isOpen ? (
        <ModalWrapper>
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalHeading
              title="Deploy Docker Image"
              description="Deploy a pre-built image from a public or private registry."
              onClose={onClose}
            />
            <Form onSubmit={onSubmit}>
              <ModalBody className="pb-0">
                {showMigrationBanner ? (
                  <Banner variant="info" className="mb-4">
                    You've previously deployed this app via Git.{" "}
                    <ExternalLink
                      href="https://www.aptible.com/docs/how-to-guides/app-guides/migrate-dockerfile-to-direct-image-deploy"
                      variant="default"
                    >
                      Review the docs for steps for migrating to Docker Image
                      before proceeding.
                    </ExternalLink>
                  </Banner>
                ) : null}
                <BannerMessages {...loader} />
                <FormGroup label="Image to deploy" htmlFor="docker-image">
                  <Input
                    id="docker-image"
                    name="docker-image"
                    type="text"
                    placeholder="e.g. nginx:latest"
                    value={dockerImage}
                    onChange={(e) => setDockerImage(e.currentTarget.value)}
                    required
                  />
                </FormGroup>
                <FormGroup label="Username" htmlFor="docker-username">
                  <Input
                    id="docker-username"
                    name="docker-username"
                    type="text"
                    placeholder="Private registry username (optional)"
                    value={registryUsername}
                    onChange={(e) => setRegistryUsername(e.currentTarget.value)}
                  />
                </FormGroup>
                <FormGroup label="Password" htmlFor="docker-password">
                  <div className="flex gap-2">
                    <Input
                      id="docker-password"
                      name="docker-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Private registry password (optional)"
                      value={registryPassword}
                      onChange={(e) =>
                        setRegistryPassword(e.currentTarget.value)
                      }
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="white"
                      onClick={() => setShowPassword((s) => !s)}
                    >
                      {showPassword ? (
                        <IconEyeClosed variant="sm" />
                      ) : (
                        <IconEyeOpen variant="sm" />
                      )}
                    </Button>
                  </div>
                </FormGroup>
              </ModalBody>
              <ModalFooter
                actions={[
                  <Button key="cancel" variant="white" onClick={onClose}>
                    Cancel
                  </Button>,
                  <Button
                    key="submit"
                    type="submit"
                    isLoading={loader.isLoading}
                  >
                    Deploy
                  </Button>,
                ]}
              />
            </Form>
          </Modal>
        </ModalWrapper>
      ) : null}
    </Box>
  );
}

function GitDeployInfo({
  gitRepo,
  appId,
  showDeployButton,
  showMigrationBanner,
}: {
  gitRepo: string;
  appId: string;
  envId: string;
  showDeployButton: boolean;
  showMigrationBanner: boolean;
}) {
  const navigate = useNavigate();
  const onDeploy = () => navigate(appDeployResumeUrl(appId));

  return (
    <Box>
      <Group size="sm">
        <div className="flex items-center justify-between">
          <h3 className={tokens.type.h3}>Deploy via Git Push</h3>
          <ButtonLinkDocs href="https://www.aptible.com/docs/core-concepts/apps/deploying-apps/image/deploying-with-git/overview" />
        </div>
        {showMigrationBanner ? (
          <Banner variant="info">
            You've previously deployed this app via Docker Image.{" "}
            <ExternalLink
              href="https://www.aptible.com/docs/how-to-guides/app-guides/deploying-docker-image-to-git"
              variant="default"
            >
              Review the docs for steps for migrating to Git before proceeding.
            </ExternalLink>
          </Banner>
        ) : null}
        <hr />
        <p>
          Add a Dockerfile to your repo, push to Aptible's Git remote, and
          Aptible builds and deploys your app automatically.
        </p>
        {showDeployButton ? (
          <div>
            <ButtonIcon
              className="w-fit"
              onClick={onDeploy}
              icon={<IconPlusCircle />}
            >
              Deploy
            </ButtonIcon>
          </div>
        ) : null}
        <div>
          <PreCode
            segments={listToInvertedTextColor(["git push", gitRepo, "main"])}
            allowCopy
          />
        </div>
      </Group>
    </Box>
  );
}

export function AppDetailDeploymentsPage() {
  const { id = "" } = useParams();
  useQuery(fetchApp({ id }));
  const app = useSelector((s) => selectAppById(s, { id }));
  const deploymentType = useSelector((s) => selectAppDeploymentType(s, { id }));
  useQuery(fetchDeploymentsByAppId({ id: app.id }));
  const deployments = useSelector((s) =>
    selectDeploymentsByAppId(s, { appId: app.id }),
  );

  const hasNoDeployments = deployments.length === 0;
  const [methodsOpen, setMethodsOpen] = useState(false);
  const methodsOpenInitialized = useRef(false);
  useEffect(() => {
    if (methodsOpenInitialized.current) return;
    if (!app.id) return;
    methodsOpenInitialized.current = true;
    setMethodsOpen(hasNoDeployments);
  }, [app.id, hasNoDeployments]);

  const isImageBased = deploymentType === "image-based";
  const imageDeployForm = app.id ? (
    <ImageDeployForm
      key="image"
      appId={id}
      settingId={app.currentSettingId}
      showMigrationBanner={!isImageBased}
    />
  ) : null;
  const gitDeployInfo = (
    <GitDeployInfo
      key="git"
      gitRepo={app.gitRepo}
      appId={id}
      envId={app.environmentId}
      showDeployButton={hasNoDeployments}
      showMigrationBanner={isImageBased}
    />
  );
  const deployMethodItems = isImageBased
    ? [imageDeployForm, gitDeployInfo]
    : [gitDeployInfo, imageDeployForm];

  return (
    <DetailPageSections>
      <Group>
        {hasNoDeployments ? (
          <div className="border border-gray-200 rounded-lg bg-white p-10 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Deployments
            </h3>
            <p className="text-gray-600 max-w-lg mx-auto">
              This app has no deployments yet. Use the Deployment Methods below
              to trigger your first deployment.
            </p>
          </div>
        ) : null}
        {deploymentType === "companion-repo" ? (
          <Banner variant="warning">
            Companion repository deployments are deprecated.{" "}
            <ExternalLink
              href="https://www.aptible.com/docs/core-concepts/apps/deploying-apps/image/deploying-with-docker-image/companion-git-repository"
              variant="default"
            >
              Learn more in the docs.
            </ExternalLink>
          </Banner>
        ) : (
          <Box>
            <Disclosure
              labelWhenClosed="Deployment Methods"
              labelWhenOpen="Deployment Methods"
              open={methodsOpen}
              onToggle={() => setMethodsOpen((o) => !o)}
              labelClassName="text-lg text-gray-900"
            >
              <Group className="mt-4">{deployMethodItems}</Group>
            </Disclosure>
          </Box>
        )}
        {!hasNoDeployments ? (
          <DeploymentsTable deployments={deployments} app={app} />
        ) : null}
      </Group>
    </DetailPageSections>
  );
}
